import { useMemo } from "react";
import type { Insured, Policy, PolicyAdditionalInsured } from "@shared/schema";
import { PowerUnitsSection } from "../assets/PowerUnitsSection";
import { TrailersSection } from "../assets/TrailersSection";
import { DriversSection } from "../assets/DriversSection";
import { AddressesSection } from "../assets/AddressesSection";
import { AdditionalInsuredsSection } from "../assets/AdditionalInsuredsSection";
import { LossPayeesSection } from "../assets/LossPayeesSection";
import { WaiversOfSubrogationSection } from "../assets/WaiversOfSubrogationSection";
import { CommoditiesSection } from "../assets/CommoditiesSection";
import {
  aggregateDrivers,
  aggregateTrailers,
  aggregateVehicles,
} from "../assets/asset-helpers";

interface Props {
  insured: Insured;
  policies: Policy[];
}

export function AssetsTab({ insured, policies }: Props) {
  // CSR-1994 — the insured-level Assets tab is the complete fleet history.
  // Historical terms remain visible; the aggregation helpers mark their rows
  // inactive or removed instead of suppressing the whole policy term.
  const powerUnits = useMemo(() => aggregateVehicles(policies), [policies]);
  const trailers = useMemo(() => aggregateTrailers(policies), [policies]);

  const drivers = useMemo(() => aggregateDrivers(policies), [policies]);

  const policyAdditionalInsureds = useMemo(() => {
    // Carrier sync writes the JSONB column as PolicyAdditionalInsured[] (CSR-743).
    // Dedup by name across all the insured's policies; the first occurrence
    // wins so we don't accidentally drop the address-bearing entry from one
    // policy in favor of a name-only entry from another.
    const seen = new Set<string>();
    const result: PolicyAdditionalInsured[] = [];
    for (const p of policies) {
      const ai = p.additionalInsureds as PolicyAdditionalInsured[] | null;
      if (!Array.isArray(ai)) continue;
      for (const item of ai) {
        if (!item?.name || seen.has(item.name)) continue;
        seen.add(item.name);
        result.push(item);
      }
    }
    return result;
  }, [policies]);

  return (
    <div className="space-y-6" data-testid="assets-tab">
      <PowerUnitsSection powerUnits={powerUnits} policies={policies} />
      <TrailersSection trailers={trailers} policies={policies} />
      <DriversSection drivers={drivers} policies={policies} />
      <AddressesSection insured={insured} />
      <AdditionalInsuredsSection
        insuredId={insured.id}
        variant="insured"
        policyAdditionalInsureds={policyAdditionalInsureds.length > 0 ? policyAdditionalInsureds : null}
        policies={policies}
      />
      <LossPayeesSection insuredId={insured.id} policies={policies} />
      <WaiversOfSubrogationSection insuredId={insured.id} policies={policies} />
      <CommoditiesSection insuredId={insured.id} />
    </div>
  );
}
